import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '../Dialog/Dialog';
import Icon from '../Icon';
import { ICONS } from '../icon-map';
import { openFresh } from './freshNav';

/**
 * Command palette (Ctrl/Cmd + K): jump to any module or run an account action.
 * items: [{ id, label, description, icon, path?, run?, group }]
 */
export default function CommandPalette({ open, onClose, items }) {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const uid = useId();

    useEffect(() => {
        if (open) {
            setQuery('');
            setActive(0);
        }
    }, [open]);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((i) => `${i.label} ${i.description || ''} ${i.group || ''}`.toLowerCase().includes(q));
    }, [items, query]);

    useEffect(() => { setActive(0); }, [query]);

    useEffect(() => {
        const el = listRef.current && listRef.current.querySelector(`[data-index="${active}"]`);
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [active]);

    const choose = (item) => {
        if (!item) return;
        onClose();
        if (item.run) item.run();
        else if (item.path) openFresh(navigate, item.path);
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); choose(results[active]); }
    };

    return (
        <Dialog open={open} onClose={onClose} size="md" hideClose initialFocusRef={inputRef} className="cmdk">
            <div className="cmdk__search">
                <Icon glyph={ICONS.search} size="sm" />
                <input
                    ref={inputRef}
                    className="cmdk__input"
                    placeholder="Go to a module or action…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    role="combobox"
                    aria-expanded="true"
                    aria-controls={`${uid}-list`}
                    aria-activedescendant={results[active] ? `${uid}-opt-${active}` : undefined}
                    aria-label="Search modules and actions"
                />
                <kbd className="ds-kbd">Esc</kbd>
            </div>
            <ul id={`${uid}-list`} ref={listRef} role="listbox" className="cmdk__list" aria-label="Results">
                {results.length === 0 && <li className="cmdk__empty">No matches for "{query}"</li>}
                {results.map((item, i) => (
                    <li
                        key={item.id}
                        id={`${uid}-opt-${i}`}
                        data-index={i}
                        role="option"
                        aria-selected={i === active}
                        className={`cmdk__item ${i === active ? 'is-active' : ''}`}
                        onMouseMove={() => setActive(i)}
                        onClick={() => choose(item)}
                    >
                        <span className="cmdk__icon"><Icon glyph={item.icon} size="sm" /></span>
                        <span className="cmdk__text">
                            <span className="cmdk__label">{item.label}</span>
                            {item.description && <span className="cmdk__desc">{item.description}</span>}
                        </span>
                        {item.group && <span className="cmdk__group">{item.group}</span>}
                    </li>
                ))}
            </ul>
        </Dialog>
    );
}
